import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle2, Circle, Timer, ArrowRight, Loader2, RefreshCcw, MapPin,
  Users, Handshake, Calendar, Eye, Sparkles,
} from "lucide-react";
import { api } from "@/lib/api";
import { TopBar } from "@/components/layout/TopBar";

const BENEFIT_ICON = {
  reseau: Users, partenariat: Handshake, rendez_vous: Calendar,
  reference: ArrowRight, visibilite: Eye, autre: Sparkles,
};

const BENEFIT_LABEL = {
  reseau: "Réseau", partenariat: "Partenariat", rendez_vous: "Rendez-vous",
  reference: "Référence", visibilite: "Visibilité", autre: "Autre",
};

export default function AbiActions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [doneIds, setDoneIds] = useState(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/abi/radar/actions/today");
      setItems(r.data?.actions || []);
    } catch (e) {
      toast.error("Impossible de charger les actions.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const markDone = async (opp) => {
    try {
      await api.post(`/abi/radar/opportunities/${opp.opportunity_id}/pipeline`,
                      { pipeline_stage: "contact_fait" });
      setDoneIds((s) => new Set([...s, opp.opportunity_id]));
      toast.success("Marquée comme faite.");
    } catch (e) { toast.error("Erreur"); }
  };

  return (
    <div data-testid="abi-actions-root">
      <TopBar
        title="Mes actions"
        subtitle="Toutes vos actions commerciales à réaliser."
      />
      <div className="px-6 md:px-10 py-8 md:py-10 max-w-[1000px] mx-auto space-y-6">

        <div className="flex items-center justify-between">
          <h2 className="font-serif text-2xl font-bold" style={{ color: "#0B2545" }}>
            Aujourd'hui ({items.length})
          </h2>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="text-xs px-3 py-1.5 rounded-lg border hover:bg-slate-50 disabled:opacity-50"
            style={{ borderColor: "#E5E7EB" }}
            data-testid="abi-actions-refresh"
          >
            {loading ? <Loader2 size={12} className="animate-spin inline" />
                     : <RefreshCcw size={12} className="inline mr-1" />}
            Actualiser
          </button>
        </div>

        {loading && items.length === 0 ? (
          <div className="p-8 flex items-center gap-2 text-slate-600">
            <Loader2 size={16} className="animate-spin" /> Chargement…
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white border rounded-xl p-8 text-center"
                style={{ borderColor: "#E5E7EB" }}>
            <p className="text-slate-600 mb-2">Aucune action en attente. 🎉</p>
            <Link to="/app/abi/radar" className="text-sm font-semibold" style={{ color: "#0B2545" }}>
              Explorer le Radar →
            </Link>
          </div>
        ) : (
          <ul className="space-y-3">
            {items.map((a, i) => {
              const done = doneIds.has(a.opportunity_id);
              const BenIcon = BENEFIT_ICON[a.expected_benefit_kind] || Sparkles;
              return (
                <li key={a.opportunity_id}
                    className={`bg-white border rounded-xl p-4 transition ${done ? "opacity-50" : "hover:shadow-md"}`}
                    style={{ borderColor: "#E5E7EB" }}
                    data-testid={`abi-actions-item-${i}`}>
                  <div className="flex items-start gap-4">
                    <button
                      type="button"
                      onClick={() => markDone(a)}
                      disabled={done}
                      className="mt-1 flex-shrink-0"
                      data-testid={`abi-actions-item-${i}-check`}
                    >
                      {done
                        ? <CheckCircle2 size={22} style={{ color: "#059669" }} />
                        : <Circle size={22} className="text-slate-400 hover:text-slate-900" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <h3 className={`font-serif text-lg font-semibold ${done ? "line-through" : ""}`}
                            style={{ color: "#0B2545" }}>{a.title_fr}</h3>
                      <p className="text-xs uppercase tracking-wide mt-1"
                          style={{ color: "#7A5B14" }}>
                        POURQUOI · <span className="normal-case tracking-normal text-slate-700 text-sm font-normal">{a.why_relevant_short_fr}</span>
                      </p>
                      <p className="text-xs uppercase tracking-wide mt-1"
                          style={{ color: "#7A5B14" }}>
                        ACTION · <span className="normal-case tracking-normal text-slate-800 text-sm font-medium">{a.recommended_action_fr}</span>
                      </p>
                      <div className="mt-3 flex items-center gap-2 flex-wrap text-xs">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                          <Timer size={11} /> {a.estimated_time_minutes} min
                        </span>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full"
                              style={{ background: "#FBF6EA", color: "#7A5B14" }}>
                          <BenIcon size={11} /> {BENEFIT_LABEL[a.expected_benefit_kind]}
                        </span>
                        <span className="inline-flex items-center gap-1 text-emerald-800">
                          <span className="font-bold">{a.priority_score}</span> / 100
                        </span>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
